<template>
  <empty-screen
    v-if="!adAccountId"
    class="my-5"
    icon="stat"
    :title="tr('Select ad account to view performance data')"
    :flag-text="tr('View your insights')"
    :description="tr('Your accounts are connected and ready for analysis. Choose a specific ad account and automation rule to see detailed metrics and discover how your automated campaigns are performing.')"
  />
  <empty-screen
    v-else-if="isListEmpty && !isLoading"
    class="my-5"
    icon="stat"
    :title="tr('Your analytics dashboard is ready and waiting for data')"
    :flag-text="tr('Analytics pending')"
    :description="tr('Your analytics dashboard will show performance metrics once your automated ads start running. Check back here after your campaigns have gathered enough data to display meaningful insights.')"
  />
  <div v-else class="contents">
    <!-- Phones: one card per ad, as in the Ads Manager apps. -->
    <div class="flex flex-col gap-2 md:hidden">
      <div class="flex items-center gap-2">
        <ui-select :model-value="orderBy" @update:model-value="(key) => sortBy(key, order)">
          <ui-select-trigger class="min-w-0 flex-1" :aria-label="tr('Sort by')">
            <span class="flex min-w-0 items-center gap-1">
              <span class="shrink-0 text-muted-foreground">{{ tr('Sort by') }}:</span>
              <ui-select-value class="truncate" />
            </span>
          </ui-select-trigger>
          <ui-select-content>
            <ui-select-item v-for="column in columns" :key="column.key" :value="column.key">
              {{ column.label }}
            </ui-select-item>
          </ui-select-content>
        </ui-select>
        <ui-button
          variant="outline"
          size="icon"
          :aria-label="order === 'asc' ? tr('Ascending') : tr('Descending')"
          @click="() => sortBy(orderBy, order === 'asc' ? 'desc' : 'asc')"
        >
          <component :is="order === 'asc' ? ArrowUpIcon : ArrowDownIcon" />
        </ui-button>
      </div>

      <div
        v-for="row in list"
        :key="'report-ad-card--' + row.external_id"
        data-slot="report-ad-card"
        class="glass-tile flex cursor-pointer flex-col gap-3 rounded-xl p-4"
        @click="() => openAdDetails(row)"
      >
        <div class="flex items-start gap-3">
          <lazy-image class="size-12 min-w-12 rounded-lg shadow-sm" :src="row.post?.image_url" />
          <div class="flex min-w-0 flex-1 flex-col gap-0.5">
            <span class="line-clamp-2 text-sm font-medium text-foreground">{{ row.name }}</span>
            <span class="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
              {{ formatToYMD(row.created_at) }}
              <ui-badge v-if="adStateFlag(row)" variant="destructive">{{ adStateFlag(row) }}</ui-badge>
            </span>
          </div>
          <div @click.stop>
            <switcher
              :model-value="isAdActive(row)"
              :disabled="isAdSwitchDisabled(row)"
              :loading="isAdStatusUpdating(row)"
              :aria-label="tr('Active')"
              @update:model-value="(val) => onChangeAdStatus(row, val)"
            />
          </div>
        </div>

        <dl :class="[ 'm-0 grid gap-x-3 gap-y-2.5 border-t border-border pt-3', isPurchaseAction ? 'grid-cols-3' : 'grid-cols-2' ]">
          <div v-for="metric in cardMetrics(row)" :key="metric.label" class="flex min-w-0 flex-col">
            <dt class="truncate text-xs text-muted-foreground">{{ metric.label }}</dt>
            <dd class="m-0 truncate text-base font-semibold text-foreground tabular-nums">{{ metric.value }}</dd>
          </div>
        </dl>
      </div>

      <template v-if="isLoading || hasMore">
        <div
          v-for="i in (list.length ? 1 : 3)"
          :key="'report-ad-card-skeleton--' + i"
          ref="scrollPagerTrigger"
          class="glass-tile flex flex-col gap-3 rounded-xl p-4"
        >
          <div class="flex items-start gap-3">
            <ui-skeleton class="size-12 rounded-lg" />
            <div class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-full" /></span>
              <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-2/3" /></span>
              <span class="flex h-4 items-center"><ui-skeleton class="h-3 w-20" /></span>
            </div>
            <ui-skeleton class="h-[1.15rem] w-8 rounded-full" />
          </div>
          <div :class="[ 'grid gap-x-3 gap-y-2.5 border-t border-border pt-3', isPurchaseAction ? 'grid-cols-3' : 'grid-cols-2' ]">
            <div v-for="col in (isPurchaseAction ? 6 : 4)" :key="col" class="flex flex-col">
              <span class="flex h-4 items-center"><ui-skeleton class="h-3 w-12" /></span>
              <span class="flex h-6 items-center"><ui-skeleton class="h-4 w-16" /></span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="glass-panel rounded-xl px-2 py-1 max-md:hidden">
      <ui-table>
        <ui-table-header>
          <ui-table-row class="hover:bg-transparent [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground">
            <ui-table-head class="w-12"><span class="sr-only">{{ tr('Status') }}</span></ui-table-head>
            <ui-table-head
              v-for="column in columns"
              :key="column.key"
              :class="column.numeric ? 'text-right' : ''"
              :aria-sort="ariaSort(column.key)"
            >
              <button
                type="button"
                data-slot="sort-button"
                class="inline-flex cursor-pointer items-center gap-1 hover:text-foreground"
                @click="() => sortBy(column.key)"
              >
                {{ column.label }}
                <component :is="sortIcon(column.key)" class="size-3.5" />
              </button>
            </ui-table-head>
            <ui-table-head class="w-10"><span class="sr-only">{{ tr('Details') }}</span></ui-table-head>
          </ui-table-row>
        </ui-table-header>
        <ui-table-body>
          <ui-table-row v-for="row in list" :key="'report-ad-table--ad-' + row.external_id">
            <ui-table-cell>
              <switcher
                :model-value="isAdActive(row)"
                :disabled="isAdSwitchDisabled(row)"
                :loading="isAdStatusUpdating(row)"
                @update:model-value="(val) => onChangeAdStatus(row, val)"
              />
            </ui-table-cell>
            <ui-table-cell class="max-w-96 min-w-60 whitespace-normal">
              <button
                type="button"
                data-slot="ad-name"
                class="line-clamp-2 cursor-pointer text-left font-medium text-foreground hover:text-primary"
                @click="() => openAdDetails(row)"
              >
                {{ row.name }}
              </button>
              <ui-badge v-if="adStateFlag(row)" variant="destructive" class="mt-1">{{ adStateFlag(row) }}</ui-badge>
            </ui-table-cell>
            <ui-table-cell class="text-right tabular-nums">{{ row.metric ? formatNumber(row.metric.reach) : '–' }}</ui-table-cell>
            <ui-table-cell class="text-right tabular-nums">{{ row.metric && row.metric.actions ? formatNumber(row.metric.actions[action]) : '–' }}</ui-table-cell>
            <ui-table-cell class="text-right tabular-nums">{{ formatMoney(calculateCpa(row)) }}</ui-table-cell>
            <ui-table-cell class="text-right tabular-nums">{{ row.metric ? formatMoney(row.metric.spend) : '–' }}</ui-table-cell>
            <template v-if="isPurchaseAction">
              <ui-table-cell class="text-right tabular-nums">{{ row.metric && row.metric.purchase_roas ? formatNumber(row.metric.purchase_roas) : '–' }}</ui-table-cell>
              <ui-table-cell class="text-right tabular-nums">{{ row.metric && row.metric.action_values && row.metric.action_values[action] ? formatMoney(row.metric.action_values[action]) : '–' }}</ui-table-cell>
            </template>
            <ui-table-cell class="tabular-nums">{{ formatToYMD(row.created_at) }} {{ formatTime(row.created_at) }}</ui-table-cell>
            <ui-table-cell>
              <ui-button
                variant="ghost"
                size="icon"
                :aria-label="tr('Details')"
                @click="() => openAdDetails(row)"
              >
                <chevron-right-icon class="text-primary" />
              </ui-button>
            </ui-table-cell>
          </ui-table-row>

          <template v-if="isLoading || hasMore">
            <ui-table-row
              v-for="i in (list.length ? 1 : 3)"
              :key="'report-ad-loader-skeleton--' + i"
              ref="scrollPagerTrigger"
              class="hover:bg-transparent"
            >
              <ui-table-cell><ui-skeleton class="h-5 w-9 rounded-full" /></ui-table-cell>
              <ui-table-cell><ui-skeleton class="h-4 w-56" /></ui-table-cell>
              <ui-table-cell v-for="col in columns.length - 1" :key="col"><ui-skeleton class="ml-auto h-4 w-12" /></ui-table-cell>
              <ui-table-cell />
            </ui-table-row>
          </template>
        </ui-table-body>
      </ui-table>
    </div>
  </div>
</template>

<script>
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from '@lucide/vue';
import { markRaw } from 'vue';

import AdStatusMixin from '@/mixins/AdStatus.js';
import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import ReportService from '@/services/Report';
import AdDetailsModal from '@/ui/components/AdDetailsModal.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import LazyImage from '@/ui/components/LazyImage.vue';
import Switcher from '@/ui/components/Switcher.vue';
import { formatTime, formatToYMD } from '@/utils/date';
import debounce from '@/utils/debounce.js';
import { formatNumber } from '@/utils/number.js';

const ITEM_PER_PAGE = 20;

export default {
  name: 'ReportAdsTable',
  components: {
    ChevronRightIcon: ChevronRight,
    EmptyScreen,
    LazyImage,
    Switcher,
  },
  mixins: [ AdStatusMixin, ScrollPagerMixin ],
  props: {
    tokenPart1: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    tokenPart2: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    adAccountId: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    campaignId: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    action: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    period: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    isActiveFilter: {
      type: Boolean,
      required: false,
    },
    currency: {
      type: String,
      required: false,
      default: () => '$',
    },
  },
  data() {
    return {
      isLoading: false,
      hasMore: true,
      page: 0,
      list: [],
      debouncedLoadData: debounce(() => {
        this.reset(); this.loadNewData();
      }, 100),
      order: 'desc',
      orderBy: 'created_at',
      ArrowDownIcon: markRaw(ArrowDown),
      ArrowUpIcon: markRaw(ArrowUp),
    };
  },
  computed: {
    isListEmpty() {
      return this.list.length == 0;
    },
    isPurchaseAction() {
      return this.action && this.action.includes('purchase');
    },
    columns() {
      const columns = [
        { key: 'name', label: this.tr('Name') },
        { key: 'metric.reach', label: this.tr('Reach'), numeric: true },
        { key: 'metric.actions.' + this.action, label: this.tr('Actions'), numeric: true },
        { key: 'metric.cost_per_action_type.' + this.action, label: this.tr('CPA'), numeric: true },
        { key: 'metric.spend', label: this.tr('Spend'), numeric: true },
      ];

      if (this.isPurchaseAction) {
        columns.push(
          { key: 'metric.purchase_roas', label: this.tr('ROAS'), numeric: true },
          { key: 'metric.action_values.' + this.action, label: this.tr('Revenue'), numeric: true },
        );
      }

      columns.push({ key: 'created_at', label: this.tr('Date created') });

      return columns;
    },
  },
  watch: {
    adAccountId() {
      this.isLoading = true;
      this.debouncedLoadData();
    },
    campaignId() {
      this.isLoading = true;
      this.debouncedLoadData();
    },
    period() {
      this.isLoading = true;
      this.debouncedLoadData();
    },
    isActiveFilter() {
      this.isLoading = true;
      this.debouncedLoadData();
    },
  },
  created() {
    this.loadNewData();
  },
  methods: {
    formatTime,
    formatToYMD,
    formatNumber,
    // Two decimals, the currency after a non-breaking space.
    formatMoney(value) {
      return formatNumber(value, '\u00a0' + this.currency, 2);
    },
    adStateFlag(row) {
      if (this.isAdDeleted(row)) {
        return this.tr('Deleted');
      }

      return this.isAdRejected(row) ? this.tr('Rejected') : null;
    },
    reset() {
      this.isLoading = false;
      this.hasMore = true;
      this.page = 0;
      this.list = [];
    },
    // Without a direction, the same column again flips it and a new column
    // starts descending (table headers); the phone sort controls pass it.
    sortBy(key, order = null) {
      this.order = order ?? (this.orderBy === key && this.order === 'desc' ? 'asc' : 'desc');
      this.orderBy = key;

      this.reset();
      this.loadNewData();
    },
    sortIcon(key) {
      if (this.orderBy !== key) {
        return ArrowUpDown;
      }

      return this.order === 'asc' ? ArrowUp : ArrowDown;
    },
    ariaSort(key) {
      if (this.orderBy !== key) {
        return 'none';
      }

      return this.order === 'asc' ? 'ascending' : 'descending';
    },
    loadNewData() {
      if (!this.hasMore || this.isLoading) {
        return;
      }
      if (!this.adAccountId || !this.period) {
        return;
      }

      this.isLoading = true;
      ++this.page;

      const params = {
        tokenPart1: this.tokenPart1,
        tokenPart2: this.tokenPart2,
        adAccountId: this.adAccountId,
        campaignId: this.campaignId,
        period: this.period,
        isActive: this.isActiveFilter,
        page: this.page,
        limit: ITEM_PER_PAGE,
        orderBy: this.orderBy,
        order: this.order,
      };
      ReportService.getAds(params)
        .then(resp => {
          this.hasMore = resp.length === ITEM_PER_PAGE;
          this.list.push(...resp);

          return resp;
        })
        .catch(err => {
          this.hasMore = false;
          this.error(err);
        })
        .finally(() => {
          this.isLoading = false;
          if (this.hasMore) {
            this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadNewData'));
          }
        });
    },
    // The figures of a phone card, in the Ads Manager apps' order.
    cardMetrics(row) {
      const metric = row.metric;
      const metrics = [
        { label: this.tr('Spend'), value: metric ? this.formatMoney(metric.spend) : '–' },
        { label: this.tr('Actions'), value: metric && metric.actions ? this.formatNumber(metric.actions[this.action]) : '–' },
        { label: this.tr('CPA'), value: this.formatMoney(this.calculateCpa(row)) },
        { label: this.tr('Reach'), value: metric ? this.formatNumber(metric.reach) : '–' },
      ];

      if (this.isPurchaseAction) {
        metrics.push(
          { label: this.tr('ROAS'), value: metric && metric.purchase_roas ? this.formatNumber(metric.purchase_roas) : '–' },
          { label: this.tr('Revenue'), value: metric && metric.action_values && metric.action_values[this.action] ? this.formatMoney(metric.action_values[this.action]) : '–' },
        );
      }

      return metrics;
    },
    calculateCpa(ad) {
      if (
        !ad
        || !ad.metric
        || !ad.metric.spend
        || !ad.metric.actions
        || !ad.metric.actions[this.action]
      ) {
        return 0;
      }

      return ad.metric.spend / ad.metric.actions[this.action];
    },
    openAdDetails(ad) {
      this.$fullscreenModal.open(this, AdDetailsModal, {
        ad,
        tokenPart1: this.tokenPart1,
        tokenPart2: this.tokenPart2,
      });
    },
  },
};
</script>
